/**
 * Advoset service-worker update manager.
 *
 * Detects a WAITING new service worker (via `reg.waiting` at load and the
 * `updatefound` lifecycle), notifies subscribers, and applies the update only
 * when the USER asks (SKIP_WAITING message → controllerchange → one reload).
 */
let _reloading = false; // reload-loop guard: reload exactly once
let _updateRequested = false; // set only when the user clicks "Update now"
const _subscribers = new Set();
let _waitingWorker = null;

function _notify(waiting) {
  _waitingWorker = waiting;
  _subscribers.forEach((cb) => {
    try { cb(waiting); } catch (_e) { /* subscriber errors never break the app */ }
  });
}

function _watchRegistration(reg) {
  if (!reg) return;
  if (reg.waiting && navigator.serviceWorker.controller) _notify(reg.waiting);
  reg.addEventListener("updatefound", () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      // "installed" + an existing controller ⇒ a NEW version is waiting.
      // (First-ever install has no controller — no prompt needed.)
      if (installing.state === "installed" && navigator.serviceWorker.controller) {
        _notify(reg.waiting || installing);
      }
    });
  });
}

export function initSwUpdater() {
  if (!("serviceWorker" in navigator)) return;
  // On the FIRST-ever install, clients.claim() also fires controllerchange —
  // never reload then. Reload only for real updates (a controller already
  // existed, or the user explicitly clicked "Update now").
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController && !_updateRequested) return;
    if (_reloading) return;
    _reloading = true;
    window.location.reload();
  });
  navigator.serviceWorker.ready.then(_watchRegistration).catch(() => {});
  // Re-check for updates when the app regains focus and every 30 minutes —
  // installed PWAs can stay open for days.
  const check = () => {
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) { _watchRegistration(reg); reg.update().catch(() => {}); }
    });
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  setInterval(check, 30 * 60 * 1000);
  // Test hook (used by automated UI tests; harmless in production)
  window.__advosetSimulateSwUpdate = () => _notify({ postMessage: () => {}, __simulated: true });
}

export function onUpdateAvailable(cb) {
  _subscribers.add(cb);
  if (_waitingWorker) cb(_waitingWorker);
  return () => _subscribers.delete(cb);
}

/** User clicked "Update now" — activate the waiting worker. */
export function applyUpdate(waiting) {
  const w = waiting || _waitingWorker;
  if (!w) { window.location.reload(); return; }
  _updateRequested = true;
  try { w.postMessage({ type: "SKIP_WAITING" }); } catch (_e) { window.location.reload(); }
  // controllerchange listener performs the single reload.
}

/** Ask the CURRENT controller for its build id (version visibility). */
export function getCurrentVersion() {
  return new Promise((resolve) => {
    const ctrl = navigator.serviceWorker?.controller;
    if (!ctrl) return resolve(null);
    const ch = new MessageChannel();
    const t = setTimeout(() => resolve(null), 1500);
    ch.port1.onmessage = (e) => { clearTimeout(t); resolve(e.data?.version || null); };
    try { ctrl.postMessage({ type: "GET_VERSION" }, [ch.port2]); } catch (_e) { resolve(null); }
  });
}
